import {NgModule, APP_INITIALIZER} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {ReactiveFormsModule} from '@angular/forms';
import {HttpClientModule, HTTP_INTERCEPTORS} from '@angular/common/http';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatCardModule} from '@angular/material/card';
import {MatInputModule} from '@angular/material/input';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatMomentDateModule, MAT_MOMENT_DATE_FORMATS, MomentDateAdapter} from '@angular/material-moment-adapter';
import {
    MatNativeDateModule,
    MAT_DATE_FORMATS,
    DateAdapter,
    MAT_DATE_LOCALE,
    MatListModule,
    MatTooltipModule,
    MatSelectModule,
    MatMenuModule,
    MatDialogModule, MatRadioModule,
    MatAutocompleteModule
} from '@angular/material';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {TextMaskModule} from 'angular2-text-mask';
import { CookieService } from 'ngx-cookie-service';

import {AppComponent} from './app.component';
import {routing} from './app.routing';
import {AlertComponent} from './directives/alert.component';
import {CookiesHeadbandComponent} from './directives/cookiesHeadband.component';
import {DisclaimerComponent} from './directives/disclaimer.component';
import {AuthGuard} from './guards/auth.guard';
import {JwtInterceptor} from './helpers/jwt.interceptor';
import {ErrorInterceptor} from './helpers/error.interceptor';
import {AlertService} from './services/alert.service';
import {AuthenticationService} from './services/authentication.service';
import {UserService} from './services/user.service';
import {HomeComponent} from './components/home/home.component';
import {LoginComponent} from './components/login/login.component';
import {RegisterComponent} from './components/register/register.component';
import {AppConfigService} from './services/app-config.service';
import {ForgetPasswordComponent} from './components/forget-password/forget-password.component';
import {PatientService} from './services/patient.service';
import {ReCaptchaDirective} from './directives/recaptcha.directive';
import {UploadModule} from './upload/upload.module';
import {UplodedDocumentComponent} from './components/uploded-document/uploded-document.component';
import {RoundPipe} from './helpers/round.pipe';
import {FormatLengthFilePipe} from './helpers/format.length.file.pipe';
import {ChangePasswordModule} from './modules/change-password/change-password.module';
import {AdminModule} from './modules/admin/admin.module';
import {GestionnaireModule} from './modules/gestionnaire/gestionnaire.module';
import {OapHtmlDownloadFile} from './helpers/oap-html-download-file';
import {UnregisterComponent} from './components/unregister/unregister.component';
import {TextesLegauxModule} from './modules/textes-legaux/textes-legaux.module';
import {ViewTextesLegauxComponent} from './modules/textes-legaux/view-textes-legaux/view-textes-legaux.component';
import {LoaderService} from './services/loader.service';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { MenusComponent } from './components/menus/menus.component';
import { TreatmentComponent } from './components/treatment/treatment/treatment.component';
import { AllergieComponent } from './components/treatment/allergie/allergie.component';
import { ItaComponent } from './components/treatment/ita/ita.component';
import { StaloralComponent } from './components/treatment/staloral/staloral.component';
import { ContreIndicationsComponent } from './components/treatment/contre-indications/contre-indications.component';
import { AdvicesComponent } from './components/treatment/advices/advices.component';
import { LinksComponent } from './components/infos/links/links.component';
import { DocumentationComponent } from './components/infos/documentation/documentation.component';
import { ContactsComponent } from './components/infos/contacts/contacts.component';
import { ToolsComponent } from './components/infos/tools/tools.component';
import { MenusTreatmentComponent } from './components/menus-treatment/menus-treatment.component';
import { MenusInfosComponent } from './components/menus-infos/menus-infos.component';
import { DisclaimerService } from './services/disclaimer.service';
import {TrackingService} from './services/tracking.service';
import { DeliveryComponent } from './components/delivery/delivery.component';
import { SupportComponent } from './components/support/support.component';
import { SupportOfflineComponent } from './components/support-offline/support-offline.component';
import { MagazineComponent } from './components/magazine/magazine.component';
import { MagazineSingleComponent } from './components/magazine-single/magazine-single.component';
import { MenusProfilComponent } from './components/menus-profil/menus-profil.component';
import { MyProfilComponent } from './components/my-profil/my-profil.component';
import { AssociatedProfilesComponent } from './components/associated-profiles/associated-profiles.component';
import { LoginDataComponent } from './components/login-data/login-data.component';
import {CompteService} from './services/compte.service';
import {CurrentPatientService} from './services/current-patient.service';
import { PopinAlertComponent } from './components/popin-alert/popin-alert.component';
import {StringPercentageToNbPipe} from './helpers/stringPercentageToNb.pipe';
import { DoubleAuthentComponent } from './components/double-authent/double-authent.component';
import { InfoRequiredFieldsComponent } from './components/info-required-fields/info-required-fields.component';
import { FaqComponent } from './components/faq/faq.component';


const appInitializerFn = (appConfig: AppConfigService) => {
    return () => {
        return appConfig.loadAppConfig();
    };
};

@NgModule({
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        HttpClientModule,
        routing,
        MatFormFieldModule,
        MatCardModule,
        MatInputModule,
        MatMomentDateModule,
        MatNativeDateModule,
        MatDatepickerModule,
        MatButtonModule,
        MatIconModule,
        MatCheckboxModule,
        MatListModule,
        MatTooltipModule,
        MatSelectModule,
        MatMenuModule,
        MatDialogModule,
        MatAutocompleteModule,
        UploadModule,
        ChangePasswordModule,
        AdminModule,
        GestionnaireModule,
        TextMaskModule,
        TextesLegauxModule,
        MatRadioModule
    ],
    declarations: [
        AppComponent,
        RoundPipe,
        FormatLengthFilePipe,
        AlertComponent,
        CookiesHeadbandComponent,
        DisclaimerComponent,
        HomeComponent,
        LoginComponent,
        RegisterComponent,
        ForgetPasswordComponent,
        ReCaptchaDirective,
        UplodedDocumentComponent,
        UnregisterComponent,
        HeaderComponent,
        FooterComponent,
        MenusComponent,
        TreatmentComponent,
        AllergieComponent,
        ItaComponent,
        StaloralComponent,
        ContreIndicationsComponent,
        AdvicesComponent,
        LinksComponent,
        DocumentationComponent,
        ContactsComponent,
        ToolsComponent,
        MenusTreatmentComponent,
        MenusInfosComponent,
        DeliveryComponent,
        SupportComponent,
        SupportOfflineComponent,
        MagazineComponent,
        MagazineSingleComponent,
        MenusProfilComponent,
        MyProfilComponent,
        AssociatedProfilesComponent,
        LoginDataComponent,
        PopinAlertComponent,
        StringPercentageToNbPipe,
        DoubleAuthentComponent,
        InfoRequiredFieldsComponent,
        FaqComponent
    ],
    entryComponents: [UnregisterComponent, ViewTextesLegauxComponent], // Add the DialogComponent as entry component
    providers: [
        AuthGuard,
        AlertService,
        LoaderService,
        DisclaimerService,
        AppConfigService,
        {
            provide: APP_INITIALIZER,
            useFactory: appInitializerFn,
            multi: true,
            deps: [AppConfigService]
        },
        AuthenticationService,
        UserService,
        PatientService,
        {provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
        // The locale would typically be provided on the root module of your application. We do it at
        // the component level here, due to limitations of our example generation script.
        {provide: MAT_DATE_LOCALE, useValue: 'fr-FR'},
        {provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE]},
        {provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS},
        OapHtmlDownloadFile,
        TrackingService,
        CookieService,
        CompteService,
        CurrentPatientService
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}
